import { reportSlugs } from '@app/ccf/company-report/slugs';
import ReportContent from '@app/components/reports/components/report-content';
import ReportMetric from '@app/components/reports/components/report-metric';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import Text from '@shared/components/content/text';
import { ChartConfig } from '@shared/components/ui/chart';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { useCCFReportResult } from '../../ccf-report-result-context';

export default function FoodAndDrink() {
  const {
    result,
    result: { scope3 },
  } = useCCFReportResult();

  const purchasedGoodsAndServices = scope3.categories.find(
    (category) => category.slug === reportSlugs.purchasedGoodsAndServices
  );

  const foodAndDrink = purchasedGoodsAndServices?.subCategories?.find(
    (subCategory) => subCategory.slug === reportSlugs.foodAndDrink
  );

  const data = {
    food_and_drink_emissions: foodAndDrink?.totalSubCategoryEmissionsTonnes,
    share_of_scope_emissions: foodAndDrink?.pctOfScopeEmissions,
    share_of_total_emissions: foodAndDrink?.pctOfCompanyEmissions,
    emissions_by_food_category:
      result.foodAndDrink?.foodEmissionsByCategory.reduce(
        (acc, category, idx) => {
          if (idx < 10) {
            acc.push({
              category: capitalizeFirstLetter(category.category),
              emissions: category.totalEmissionsTonnes,
              fill: 'var(--chart-3)',
            });
          }
          return acc;
        },
        [] as { category: string; emissions: number; fill: string }[]
      ),
  };

  const chartConfig = {
    emissions: {
      label: 'Emissions',
      color: 'hsl(var(--chart-1))',
      unit: 'tCO2e',
    },
  } satisfies ChartConfig;
  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          This category includes all the food and drink your business buys. For
          food businesses, this is often the biggest contributor to overall
          emissions due to the energy and resources required in farming,
          production, and transportation.
        </Text>
        <ReportMetric
          title="Category emissions"
          value={data.food_and_drink_emissions!}
          unit="tCO2e"
          size="lg"
        />
        <div className="flex space-x-2">
          <ReportMetric
            title="Share of Scope 3 emissions"
            value={data.share_of_scope_emissions!}
            unit="%"
            size="md"
            className="flex-1"
          />
          <ReportMetric
            title="Share of total emissions"
            value={data.share_of_total_emissions!}
            unit="%"
            size="md"
            className="flex-1"
          />
        </div>
      </>
      <>
        <Text variant="h3">Emissions by food category</Text>
        <SimpleBarChart
          nameKey="category"
          dataKey="emissions"
          chartData={data.emissions_by_food_category}
          chartConfig={chartConfig}
          className="size-full max-h-[calc(var(--content-title-height)-15rem)]"
          layout="vertical"
          margin={{ left: 20, right: 80 }}
          XAxisProps={{
            label: {
              value: 'tCO2e',
              position: 'right',
              offset: 20,
            },
          }}
        />
      </>
    </ReportContent>
  );
}
